import { useState, useEffect } from 'react';
import TemaLogin from './login';
import TemaLayout from './components/TemaLayout';
import GeneralSection from './components/sections/GeneralSection';
import SliderSection from './components/sections/SliderSection';
import FeaturesSectionEditor from './components/sections/FeaturesSection';
import ContactSection from './components/sections/ContactSection';
import SocialSection from './components/sections/SocialSection';
import FooterSection from './components/sections/FooterSection';
import StorySection from './components/sections/StorySection';
import NewsletterSection from './components/sections/NewsletterSection';
import CampaignSection from './components/sections/CampaignSection';
import NavbarSection from './components/sections/NavbarSection';
import TestimonialsSectionEditor from './components/sections/TestimonialsSection';
import PagesSection from './components/sections/PagesSection';
import { getMergedTheme, saveThemeConfig, ThemeConfig, THEME_CHANGE_EVENT } from '@/lib/themeConfig';

export default function TemaPage() {
  const [authed, setAuthed] = useState(false);
  const [section, setSection] = useState('general');
  const [theme, setTheme] = useState<ThemeConfig>(() => getMergedTheme());

  useEffect(() => {
    if (sessionStorage.getItem('tema_auth') === 'true') setAuthed(true);
  }, []);

  // Keep in sync if another tab changes theme
  useEffect(() => {
    const handler = () => setTheme(getMergedTheme());
    window.addEventListener('storage', handler);
    window.addEventListener(THEME_CHANGE_EVENT, handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener(THEME_CHANGE_EVENT, handler);
    };
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('tema_auth');
    setAuthed(false);
  };

  const update = (partial: Partial<ThemeConfig>) => {
    const next = { ...theme, ...partial };
    setTheme(next);
    saveThemeConfig(next);
  };

  if (!authed) return <TemaLogin onLogin={() => setAuthed(true)} />;

  return (
    <TemaLayout activeSection={section} onSectionChange={setSection} onLogout={handleLogout}>
      {section === 'general' && <GeneralSection data={theme.general} onChange={d => update({ general: d })} />}
      {section === 'navbar' && <NavbarSection data={theme.navbar} onChange={d => update({ navbar: d })} />}
      {section === 'slider' && <SliderSection data={theme.slider} onChange={d => update({ slider: d })} />}
      {section === 'features' && <FeaturesSectionEditor data={theme.features} onChange={d => update({ features: d })} />}
      {section === 'story' && <StorySection data={theme.story} onChange={d => update({ story: d })} />}
      {section === 'newsletter' && <NewsletterSection data={theme.newsletter} onChange={d => update({ newsletter: d })} />}
      {section === 'testimonials' && <TestimonialsSectionEditor data={theme.testimonials} onChange={d => update({ testimonials: d })} />}
      {section === 'campaign' && <CampaignSection data={theme.campaign} onChange={d => update({ campaign: d })} />}
      {section === 'contact' && <ContactSection data={theme.contact} onChange={d => update({ contact: d })} />}
      {section === 'social' && <SocialSection data={theme.social} onChange={d => update({ social: d })} />}
      {section === 'footer' && <FooterSection data={theme.footer} onChange={d => update({ footer: d })} />}
      {section === 'pages' && <PagesSection pages={theme.pages ?? []} onChange={p => update({ pages: p })} />}
    </TemaLayout>
  );
}
